import { useState, useEffect, useRef, useCallback } from "react";
import type { Blackboard, TaggedValue } from "../engine/Blackboard";
import { untagValue } from "../engine/Blackboard";
import type { EventBus, EventMap } from "../engine/EventBus";
import type { Interpreter } from "../engine/Interpreter";
import type { RuleDefinition } from "../engine/RuleEngine";
import { StoryGraph } from "./StoryGraph";
import { DiagnosticsPanel } from "./DiagnosticsPanel";

interface DebugPanelProps {
  bb: Blackboard;
  bus: EventBus;
  interpreter: Interpreter;
}

type Tab = "variables" | "events" | "rules" | "scenes" | "graph" | "diag";

interface EventLogEntry {
  id: number;
  ts: number;
  event: string;
  data: unknown;
}

const MAX_EVENT_LOG = 200;

export function DebugPanel({ bb, bus, interpreter }: DebugPanelProps) {
  const [tab, setTab] = useState<Tab>("variables");
  const [variables, setVariables] = useState<Record<string, TaggedValue>>({});
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([]);
  const [filter, setFilter] = useState("");
  const eventIdRef = useRef(0);
  const logEndRef = useRef<HTMLDivElement>(null);

  // Refresh variables from Blackboard snapshot
  const refreshVars = useCallback(() => {
    setVariables(bb.snapshot());
  }, [bb]);

  useEffect(() => {
    refreshVars();
    const handler = () => refreshVars();
    bb.listenAll(handler);
    return () => bb.removeGlobalListener(handler);
  }, [bb, refreshVars]);

  // Subscribe to all EventBus events for the event log
  useEffect(() => {
    const events: (keyof EventMap)[] = [
      "dialogue.narrate", "dialogue.say", "choice.show", "choice.select",
      "advance", "story.end", "var.set", "bg.set", "sprite.show", "sprite.hide",
      "audio.play", "audio.stop", "wait.start", "wait.end", "call.exec",
      "video.play", "video.end", "effect.run", "animation.run", "popup.show",
      "scene.load", "rollback.snapshot", "rollback.done", "save.done", "load.done",
      "scene.ready",
    ];
    const unsubs: (() => void)[] = [];
    for (const evt of events) {
      const unsub = bus.on(evt, (data: unknown) => {
        const entry: EventLogEntry = {
          id: ++eventIdRef.current,
          ts: Date.now(),
          event: evt,
          data,
        };
        setEventLog((prev) => {
          const next = [...prev, entry];
          return next.length > MAX_EVENT_LOG ? next.slice(-MAX_EVENT_LOG) : next;
        });
      });
      unsubs.push(unsub);
    }
    return () => unsubs.forEach((u) => u());
  }, [bus]);

  // Auto-scroll event log
  useEffect(() => {
    if (tab === "events") {
      logEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [eventLog, tab]);

  const program = interpreter.getProgram();
  const scenes = program ? Object.keys(program.scenes) : [];
  const labels = program ? Object.keys(program.labels) : [];

  const handleTeleport = (scene: string) => {
    interpreter.teleport(scene);
  };

  const handleTeleportLabel = (labelKey: string) => {
    if (!program) return;
    const ref = program.labels[labelKey];
    if (ref) interpreter.teleport(ref.scene, ref.beat);
  };

  // Filter variables
  const varEntries = Object.entries(variables).filter(
    ([key]) => !filter || key.toLowerCase().includes(filter.toLowerCase()),
  );

  const rules: RuleDefinition[] = program?.globals?.rules ?? [];

  const panelWidth = tab === "graph" ? "750px" : "320px";

  return (
    <div style={{ ...panelStyle, width: panelWidth }}>
      <div style={headerStyle}>
        <span style={{ fontWeight: "bold", fontSize: "0.85rem" }}>Debug</span>
        <span style={locationStyle}>
          {interpreter.currentScene}:{interpreter.currentBeat}
        </span>
      </div>

      <div style={tabBarStyle}>
        {(["variables", "events", "rules", "scenes", "graph", "diag"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={tab === t ? tabActiveStyle : tabStyle}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={bodyStyle}>
        {tab === "variables" && (
          <div>
            <input
              type="text"
              placeholder="Filter..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={filterStyle}
            />
            <div style={scrollStyle}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {varEntries.map(([key, tv]) => (
                    <tr key={key}>
                      <td style={tdStyle}>{key}</td>
                      <td style={tdStyle}>{tv.type}</td>
                      <td style={tdStyle}>{formatTagged(tv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div style={scrollStyle}>
            {eventLog.map((entry) => (
              <div key={entry.id} style={eventRowStyle}>
                <span style={eventTimeStyle}>
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
                <span style={eventNameStyle}>{entry.event}</span>
                {entry.data !== undefined && (
                  <span style={eventDataStyle}>
                    {JSON.stringify(entry.data, null, 0)?.slice(0, 120)}
                  </span>
                )}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}

        {tab === "rules" && (
          <div style={scrollStyle}>
            {rules.length === 0 && (
              <div style={{ opacity: 0.5, padding: "1rem" }}>No rules defined</div>
            )}
            {rules.map((rule, i) => (
              <div key={i} style={ruleRowStyle}>
                <span style={{ fontWeight: "bold" }}>{rule.name}</span>
                <span style={{ marginLeft: "0.5rem", opacity: 0.7, fontSize: "0.7rem" }}>
                  triggers: {rule.triggers.join(", ")}
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === "scenes" && (
          <div style={scrollStyle}>
            <div style={{ marginBottom: "0.5rem", fontSize: "0.75rem", opacity: 0.7 }}>
              Scenes ({scenes.length})
            </div>
            {scenes.map((s) => (
              <div key={s} style={sceneRowStyle}>
                <button onClick={() => handleTeleport(s)} style={teleportBtnStyle}>
                  {s}
                </button>
                <span style={visitStyle}>
                  visits: {interpreter.getVisits(s)}
                </span>
              </div>
            ))}
            {labels.length > 0 && (
              <>
                <div style={{ marginTop: "0.75rem", marginBottom: "0.5rem", fontSize: "0.75rem", opacity: 0.7 }}>
                  Labels ({labels.length})
                </div>
                {labels.map((l) => (
                  <div key={l} style={sceneRowStyle}>
                    <button onClick={() => handleTeleportLabel(l)} style={teleportBtnStyle}>
                      {l}
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab === "graph" && program && (
          <StoryGraph program={program} currentScene={interpreter.currentScene} />
        )}

        {tab === "diag" && (
          <DiagnosticsPanel program={program} />
        )}
      </div>
    </div>
  );
}

function formatTagged(tv: TaggedValue): string {
  const raw = untagValue(tv);
  if (typeof raw === "object" && raw !== null) return JSON.stringify(raw);
  return String(raw);
}

// --- Styles ---

const panelStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  right: 0,
  width: "320px",
  height: "100%",
  background: "rgba(10, 10, 20, 0.92)",
  color: "#e0e0e0",
  fontFamily: "monospace",
  fontSize: "0.8rem",
  display: "flex",
  flexDirection: "column",
  zIndex: 9999,
  borderLeft: "1px solid rgba(255,255,255,0.1)",
  pointerEvents: "auto",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.5rem 0.75rem",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
};

const locationStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  opacity: 0.7,
  background: "rgba(255,255,255,0.05)",
  padding: "0.15rem 0.4rem",
  borderRadius: "3px",
};

const tabBarStyle: React.CSSProperties = {
  display: "flex",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
};

const tabStyle: React.CSSProperties = {
  flex: 1,
  padding: "0.4rem",
  background: "transparent",
  color: "#888",
  border: "none",
  cursor: "pointer",
  fontSize: "0.7rem",
  textTransform: "uppercase",
};

const tabActiveStyle: React.CSSProperties = {
  ...tabStyle,
  color: "#7ec8e3",
  borderBottom: "2px solid #7ec8e3",
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const scrollStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "0.5rem",
};

const filterStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.3rem 0.5rem",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "3px",
  color: "#e0e0e0",
  fontSize: "0.75rem",
  outline: "none",
  boxSizing: "border-box",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.2rem 0.4rem",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
  fontSize: "0.7rem",
  opacity: 0.6,
};

const tdStyle: React.CSSProperties = {
  padding: "0.2rem 0.4rem",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  wordBreak: "break-all",
  maxWidth: "120px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const eventRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.4rem",
  padding: "0.2rem 0",
  borderBottom: "1px solid rgba(255,255,255,0.03)",
  alignItems: "baseline",
  fontSize: "0.7rem",
};

const eventTimeStyle: React.CSSProperties = {
  opacity: 0.4,
  whiteSpace: "nowrap",
  fontSize: "0.65rem",
};

const eventNameStyle: React.CSSProperties = {
  color: "#7ec8e3",
  whiteSpace: "nowrap",
};

const eventDataStyle: React.CSSProperties = {
  opacity: 0.6,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ruleRowStyle: React.CSSProperties = {
  padding: "0.3rem 0",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
};

const sceneRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.2rem 0",
};

const teleportBtnStyle: React.CSSProperties = {
  background: "rgba(126, 200, 227, 0.1)",
  border: "1px solid rgba(126, 200, 227, 0.3)",
  color: "#7ec8e3",
  padding: "0.15rem 0.5rem",
  borderRadius: "3px",
  cursor: "pointer",
  fontSize: "0.7rem",
};

const visitStyle: React.CSSProperties = {
  fontSize: "0.65rem",
  opacity: 0.5,
};
